/* eslint-disable react-hooks/exhaustive-deps */
import React, { useCallback, useEffect, useState } from 'react'

import { MarkerComponent } from '@libs/apryse/components/Marker/Marker.component'
import { WEB_VIEWER_CORE_PATH } from '@libs/apryse/constants'
import { Marker } from '@libs/apryse/type'

import { Loader } from '@components/Loader'

import { LRUCacheWithTTL } from '@utils/cache'
import { useWindowSize } from '@utils/hooks'

import { Container, FakeContainer, ImageContainer, LoaderContainer } from './PdfMarkerPreview.styled'

const pdfImageCache = new LRUCacheWithTTL()

interface PdfMarkerPreviewProps {
  pdfUrl: string
  initialMarker: Marker
  height?: number
}

const CANVAS_CONFIG = {
  pageNumber: 1,
  zoom: 1,
  allowUseOfOptimizedThumbnail: true,
  useProgress: true,
  multiplier: {
    mobile: 0.2,
    desktop: 0.4,
  },
} as const

const IMAGE_CONFIG = {
  format: 'image/jpeg' as const,
  quality: 0.7,
}

export const PdfMarkerPreview: React.FC<PdfMarkerPreviewProps> = ({ pdfUrl, height = 300, initialMarker }) => {
  const { isMobile } = useWindowSize()

  const [preview, setPreview] = useState<string | null>()

  const getCacheKey = useCallback((url: string): string => {
    const configHash = JSON.stringify({ ...CANVAS_CONFIG, ...IMAGE_CONFIG })
    return `${url}_${btoa(configHash).replace(/[+/=]/g, '')}`
  }, [])

  const handleCanvasLoad = useCallback(
    (loadedElement: HTMLCanvasElement | HTMLImageElement) => {
      let imageDataUrl: string

      if (loadedElement instanceof HTMLCanvasElement) {
        imageDataUrl = loadedElement.toDataURL(IMAGE_CONFIG.format, IMAGE_CONFIG.quality)
      } else {
        imageDataUrl = loadedElement.src
      }

      const cacheKey = getCacheKey(pdfUrl)
      pdfImageCache.set(cacheKey, imageDataUrl)
      setPreview(imageDataUrl)
    },
    [pdfUrl, getCacheKey],
  )

  const loadPreview = useCallback(async () => {
    const cacheKey = getCacheKey(pdfUrl)
    const cachedImage = pdfImageCache.get(cacheKey)

    if (cachedImage) {
      setPreview(cachedImage)
      return
    }

    const Core = (window as any).Core

    Core.setWorkerPath(WEB_VIEWER_CORE_PATH)

    const doc = await Core.createDocument(pdfUrl, {
      licenseKey: import.meta.env.VITE_FEATURE_APRYSE_API_KEY,
      shouldUseMinimumDownloads: true,
      useDownloader: false,
    })

    doc.loadCanvas({
      ...CANVAS_CONFIG,
      multiplier: isMobile ? CANVAS_CONFIG.multiplier.mobile : CANVAS_CONFIG.multiplier.desktop,
      drawProgressive: handleCanvasLoad,
      drawComplete: handleCanvasLoad,
    })
  }, [pdfUrl])

  useEffect(() => {
    loadPreview()
  }, [pdfUrl])

  return (
    <>
      {preview ? (
        <Container>
          <FakeContainer />

          <ImageContainer height={height} markerX={initialMarker.x} markerY={initialMarker.y}>
            {preview && <img src={preview} height={height} alt="Preview" />}
            <MarkerComponent marker={initialMarker} isSelected={false} isDragging={false} canEdit={false} />
          </ImageContainer>
        </Container>
      ) : (
        <LoaderContainer height={height}>
          <Loader size={20} />
        </LoaderContainer>
      )}
    </>
  )
}
